import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import ScienceIcon from "@mui/icons-material/Science";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Navbar from "../Components/Navbar/navbar";
import { auth, db } from "../Config/firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";
import RelatorioMedicaoPrint from "./RelatorioMedicaoPrint";

const hoje = () => new Date().toISOString().split("T")[0];

const estadoInicial = {
  codigo: "",
  data: hoje(),
  dataInicial: "",
  dataFinal: hoje(),
  notificarAposDias: "",
  notificacaoCancelada: false,
  cliente: "",
  clienteId: "",
  obra: "",
  obraId: "",
  idadesSelecionadas: [],
  linhas: [],
  itensAdicionais: [],
  periodoOutrosServicosInicio: "",
  periodoOutrosServicosFim: "",
  referenciaRelatorio: "",
  observacoes: "",
};

function numero(valor) {
  if (valor === "" || valor === null || valor === undefined) return 0;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;
  const convertido = Number(String(valor).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(convertido) ? convertido : 0;
}

function moeda(valor) {
  return numero(valor).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatarValorUnitario(valor) {
  if (String(valor ?? "").trim() === "") return "";

  return numero(valor).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function dataBR(valor) {
  if (!valor) return "-";
  const [ano, mes, dia] = String(valor).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
}

function calcularPrevisaoProximaMedicao(dataAte, quantidadeDias) {
  if (!dataAte || quantidadeDias === "" || quantidadeDias === null) return "-";

  const dias = Number(quantidadeDias);
  const partes = String(dataAte).split("-").map(Number);
  if (partes.length !== 3 || !Number.isInteger(dias) || dias < 0) return "-";

  const [ano, mes, dia] = partes;
  const previsao = new Date(ano, mes - 1, dia);
  if (Number.isNaN(previsao.getTime())) return "-";

  previsao.setDate(previsao.getDate() + dias);
  return dataBR(
    `${previsao.getFullYear()}-${String(previsao.getMonth() + 1).padStart(2, "0")}-${String(
      previsao.getDate()
    ).padStart(2, "0")}`
  );
}

function criadoEmMs(valor) {
  if (valor?.toMillis) return valor.toMillis();
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? 0 : data.getTime();
}

function primeiraNotaFiscal(moldagem = {}) {
  const caminhoes = [...(moldagem.caminhoes || [])].sort(
    (a, b) => Number(a?.cod_caminhao || 0) - Number(b?.cod_caminhao || 0)
  );
  const nota = caminhoes.find((item) => String(item?.notaFiscalRomaneio || "").trim())
    ?.notaFiscalRomaneio;
  const valor = Number(String(nota || "").replace(/\D/g, ""));
  return Number.isFinite(valor) && valor > 0 ? valor : Number.MAX_SAFE_INTEGER;
}

function ordenarMoldagens(a, b) {
  const porData = String(a.datamoldagem || "").localeCompare(String(b.datamoldagem || ""));
  if (porData !== 0) return porData;
  const porNota = primeiraNotaFiscal(a) - primeiraNotaFiscal(b);
  if (porNota !== 0) return porNota;
  return criadoEmMs(a.criadoEm) - criadoEmMs(b.criadoEm);
}

function normalizarIdade(idade) {
  return String(idade?.dias ?? idade ?? "").trim();
}

function normalizarTexto(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("pt-BR");
}

function dentroDoPeriodo(data, inicio, fim) {
  if (inicio && data < inicio) return false;
  if (fim && data > fim) return false;
  return true;
}

function observacaoIdades(moldagem, idades, dataMedicao) {
  if (!moldagem?.datamoldagem || !dataMedicao) return "";
  const pendentes = idades.filter((idade) => {
    const dias = Number(idade);
    if (!Number.isFinite(dias)) return false;
    const ruptura = new Date(`${moldagem.datamoldagem}T00:00:00`);
    ruptura.setDate(ruptura.getDate() + dias);
    return ruptura > new Date(`${dataMedicao}T23:59:59`);
  });
  return pendentes.length ? `FALTA ${pendentes.join(", ")} DIAS` : "";
}

function resultadoRupturaPreenchido(valor) {
  return valor !== null && valor !== undefined && String(valor).trim() !== "";
}

function chaveCpAgenda(agendaRupturaId, idade) {
  return `${agendaRupturaId}|${idade}`;
}

function obterConsumoAnterior(medicoes, obraId, medicaoAtualId) {
  const cpIdsConsumidos = new Map();
  const quantidadesLegadas = new Map();

  (medicoes || [])
    .filter((medicao) => medicao.id !== medicaoAtualId && medicao.obraId === obraId)
    .forEach((medicao) => {
      (medicao.linhas || []).forEach((linha) => {
        Object.entries(linha.quantidades || {}).forEach(([idade, quantidade]) => {
          if (numero(quantidade) <= 0 || !linha.agendaRupturaId) return;
          const cpIds = linha.cpIdsPorIdade?.[idade];

          if (Array.isArray(cpIds) && cpIds.length) {
            const chaveCp = chaveCpAgenda(linha.agendaRupturaId, idade);
            const ids = cpIdsConsumidos.get(chaveCp) || new Set();
            cpIds.forEach((cpId) => ids.add(cpId));
            cpIdsConsumidos.set(chaveCp, ids);
          } else {
            const chave = chaveCpAgenda(linha.agendaRupturaId, idade);
            quantidadesLegadas.set(
              chave,
              (quantidadesLegadas.get(chave) || 0) + numero(quantidade)
            );
          }
        });
      });
    });

  return { cpIdsConsumidos, quantidadesLegadas };
}

function criarLinhasAgenda(
  moldagens,
  obra,
  idadesSelecionadas,
  dataInicial,
  dataFinal,
  dataMedicao,
  medicoes = [],
  medicaoAtualId = ""
) {
  let codigoPagina = Number(obra?.codigoInicial || 0);
  const linhas = [];
  const consumoAnterior = obterConsumoAnterior(medicoes, obra?.id, medicaoAtualId);

  [...moldagens].sort(ordenarMoldagens).forEach((moldagem) => {
    const caminhoes = [...(moldagem.caminhoes || [])].sort(
      (a, b) => Number(a.cod_caminhao || 0) - Number(b.cod_caminhao || 0)
    );
    const limitePagina =
      moldagem.incluirObservacaoRelatorio === true &&
      String(moldagem.observacaoRelatorio || "").trim()
        ? 13
        : 14;
    const quantidadeCadastrada = Math.max(
      Number(moldagem.qtdCaminhoes || 0),
      caminhoes.length
    );
    const totalPaginas = Math.ceil(quantidadeCadastrada / limitePagina);
    const idadesMoldagem = (moldagem.idades || []).map(normalizarIdade).filter(Boolean);
    const codigoInicialMoldagem = codigoPagina + 1;
    codigoPagina += totalPaginas;

    if (!totalPaginas || !dentroDoPeriodo(moldagem.datamoldagem || "", dataInicial, dataFinal)) {
      return;
    }

    const caminhoesAtivos = caminhoes.filter((item) => item.status !== "cancelado");
    const quantidades = {};
    const cpIdsPorIdade = {};

    idadesSelecionadas.forEach((idade) => {
      const idadeIndex = idadesMoldagem.indexOf(String(idade));
      if (idadeIndex < 0) {
        quantidades[idade] = 0;
        cpIdsPorIdade[idade] = [];
        return;
      }

      const cpIdsRompidos = caminhoesAtivos.flatMap((caminhao) => {
        const resultado = caminhao.resultados?.[idadeIndex];
        const caminhaoId = caminhao.id || String(caminhao.cod_caminhao || "");
        return ["cp1", "cp2"]
          .filter((campo) => resultadoRupturaPreenchido(resultado?.[campo]))
          .map((campo) => `${caminhaoId}:${campo}`);
      });
      const chave = chaveCpAgenda(moldagem.id, idade);
      const idsJaMedidos = consumoAnterior.cpIdsConsumidos.get(chave) || new Set();
      const quantidadeLegada = consumoAnterior.quantidadesLegadas.get(chave) || 0;
      const cpIdsDisponiveis = cpIdsRompidos
        .filter((cpId) => !idsJaMedidos.has(cpId))
        .slice(quantidadeLegada);

      cpIdsPorIdade[idade] = cpIdsDisponiveis;
      quantidades[idade] = cpIdsDisponiveis.length;
    });

    if (!Object.values(quantidades).some((quantidade) => quantidade > 0)) return;

    linhas.push({
      id: moldagem.id,
      agendaRupturaId: moldagem.id,
      paginas: Array.from({ length: totalPaginas }, (_, index) => index),
      codigo:
        totalPaginas > 1
          ? `${codigoInicialMoldagem} a ${codigoPagina}`
          : codigoInicialMoldagem,
      dataMoldagem: moldagem.datamoldagem || "",
      quantidades,
      cpIdsPorIdade,
      valorUnitario: "",
      observacao: observacaoIdades(
        moldagem,
        idadesSelecionadas.filter((idade) => idadesMoldagem.includes(String(idade))),
        dataMedicao
      ),
    });
  });

  return linhas;
}

function totalQuantidadeLinha(linha, idades) {
  return idades.reduce((total, idade) => total + numero(linha.quantidades?.[idade]), 0);
}

function totalLinha(linha, idades) {
  return totalQuantidadeLinha(linha, idades) * numero(linha.valorUnitario);
}

function totalItem(item) {
  return numero(item.quantidade) * numero(item.valorUnitario);
}

function moverParaCampoAbaixo(evento, coluna) {
  if (evento.key !== "Enter") return;

  evento.preventDefault();
  const campos = Array.from(
    document.querySelectorAll(`[data-medicao-coluna="${coluna}"]`)
  ).filter((campo) => !campo.disabled);
  const indiceAtual = campos.indexOf(evento.target);
  const proximoCampo = campos[indiceAtual + 1];

  if (proximoCampo) {
    proximoCampo.focus();
    proximoCampo.select?.();
  }
}

export default function CadastroMedicoes() {
  const { medicaoId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const medicaoParaClonarId = new URLSearchParams(location.search).get("clonar");
  const [usuario, setUsuario] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [medicoes, setMedicoes] = useState([]);
  const [form, setForm] = useState(estadoInicial);
  const [medicaoEditando, setMedicaoEditando] = useState(null);
  const [moldagensObra, setMoldagensObra] = useState([]);
  const [carregandoAgenda, setCarregandoAgenda] = useState(false);
  const [agendaConsultada, setAgendaConsultada] = useState(false);
  const [replicarValorUnitarioAgenda, setReplicarValorUnitarioAgenda] = useState(false);
  const [modulosDisponiveis, setModulosDisponiveis] = useState([]);
  const [carregandoModulos, setCarregandoModulos] = useState(false);
  const [filtroModulos, setFiltroModulos] = useState("");
  const [dataInicialModulos, setDataInicialModulos] = useState("");
  const [dataFinalModulos, setDataFinalModulos] = useState("");
  const [itemModuloIndex, setItemModuloIndex] = useState(null);
  const [medicaoPrint, setMedicaoPrint] = useState(null);
  const [formSalvo, setFormSalvo] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [carregandoCadastro, setCarregandoCadastro] = useState(Boolean(medicaoId || medicaoParaClonarId));
  const buscaAgendaClonagemIniciada = useRef(false);
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();

  useEffect(() => onAuthStateChanged(auth, setUsuario), []);

  useEffect(() => {
    async function carregarBase() {
      try {
        const [clientesSnap, medicoesSnap] = await Promise.all([
          getDocs(consultaTenantAtual(query(collection(db, "clientes"), orderBy("nome", "asc")))),
          getDocs(consultaTenantAtual(collection(db, "tblMedicoes"))),
        ]);
        setClientes(clientesSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
        const listaMedicoes = medicoesSnap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => String(b.data || "").localeCompare(String(a.data || "")));
        setMedicoes(listaMedicoes);

        if (medicaoId) {
          const medicaoSnap = await getDoc(doc(db, "tblMedicoes", medicaoId));
          if (!medicaoSnap.exists()) {
            history.replace("/app/medicoes");
            return;
          }
          const medicao = { id: medicaoSnap.id, ...medicaoSnap.data() };
          await carregarObras(medicao.clienteId);
          const formCarregado = { ...estadoInicial, ...medicao };
          setMedicaoEditando(medicao);
          setForm(formCarregado);
          setFormSalvo(formCarregado);
          setReplicarValorUnitarioAgenda(false);
          if (new URLSearchParams(location.search).get("relatorio") === "true") {
            setMedicaoPrint(medicao);
          }
        } else if (medicaoParaClonarId) {
          const medicao = listaMedicoes.find((item) => item.id === medicaoParaClonarId);
          if (!medicao) {
            history.replace("/app/medicoes");
            return;
          }

          await carregarObras(medicao.clienteId);
          setMedicaoEditando(null);
          setForm({
            ...estadoInicial,
            codigo: "",
            data: hoje(),
            cliente: medicao.cliente || "",
            clienteId: medicao.clienteId || "",
            obra: medicao.obra || "",
            obraId: medicao.obraId || "",
            dataInicial: medicao.dataFinal || "",
            dataFinal: hoje(),
            notificarAposDias: medicao.notificarAposDias ?? "",
            notificacaoCancelada: false,
            itensAdicionais: (medicao.itensAdicionais || []).map((item) => ({
              ...item,
              moduloIds: [...(item.moduloIds || [])],
              codigosModulo: [...(item.codigosModulo || [])],
            })),
            periodoOutrosServicosInicio: medicao.periodoOutrosServicosInicio || "",
            periodoOutrosServicosFim: medicao.periodoOutrosServicosFim || "",
            referenciaRelatorio: medicao.referenciaRelatorio || "",
            observacoes: medicao.observacoes || "",
          });
          setFormSalvo(null);
          setReplicarValorUnitarioAgenda(false);
        }
      } catch (error) {
        console.error("Erro ao carregar medições:", error);
      } finally {
        setCarregandoCadastro(false);
      }
    }
    carregarBase();
  }, [medicaoId, medicaoParaClonarId, history, location.search]);

  async function carregarObras(clienteId) {
    setObras([]);
    if (!clienteId) return;
    const snap = await getDocs(
      consultaTenantAtual(query(collection(db, "obras"), where("clienteId", "==", clienteId), orderBy("nome", "asc")))
    );
    setObras(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }

  async function selecionarCliente(cliente) {
    setForm((atual) => ({
      ...atual,
      cliente: cliente?.nome || "",
      clienteId: cliente?.id || "",
      obra: "",
      obraId: "",
      idadesSelecionadas: [],
      linhas: [],
    }));
    setMoldagensObra([]);
    setAgendaConsultada(false);
    await carregarObras(cliente?.id || "");
  }

  function selecionarObra(obra) {
    setForm((atual) => ({
      ...atual,
      obra: obra?.nome || "",
      obraId: obra?.id || "",
      idadesSelecionadas: [],
      linhas: [],
    }));
    setMoldagensObra([]);
    setAgendaConsultada(false);
  }

  async function carregarAgenda() {
    if (!form.obraId) {
      alert("Selecione a obra antes de buscar a Agenda de Ruptura.");
      return;
    }
    if (!form.dataInicial || !form.dataFinal) {
      alert("Informe o período completo das moldagens antes de buscar a Agenda de Ruptura.");
      return;
    }

    setCarregandoAgenda(true);
    try {
      const snap = await getDocs(
        consultaTenantAtual(query(collection(db, "tblAgendaRuptura"), where("obraId", "==", form.obraId)))
      );
      const moldagens = await Promise.all(
        snap.docs.map(async (moldagemDoc) => {
          const caminhoesSnap = await getDocs(
            collection(db, "tblAgendaRuptura", moldagemDoc.id, "caminhoes")
          );
          return {
            id: moldagemDoc.id,
            ...moldagemDoc.data(),
            caminhoes: caminhoesSnap.docs.map((item) => ({ id: item.id, ...item.data() })),
          };
        })
      );
      const idades = Array.from(
        new Set(
          moldagens.flatMap((moldagem) =>
            (moldagem.idades || []).map(normalizarIdade).filter(Boolean)
          )
        )
      ).sort((a, b) => numero(a) - numero(b));
      setMoldagensObra(moldagens);
      setAgendaConsultada(true);
      setForm((atual) => ({
        ...atual,
        idadesSelecionadas: idades,
        linhas: criarLinhasAgenda(
          moldagens,
          obras.find((item) => item.id === atual.obraId),
          idades,
          atual.dataInicial,
          atual.dataFinal,
          atual.data,
          medicoes,
          medicaoEditando?.id
        ),
      }));
    } catch (error) {
      console.error("Erro ao buscar Agenda de Ruptura:", error);
      alert("Não foi possível carregar a Agenda de Ruptura desta obra.");
    } finally {
      setCarregandoAgenda(false);
    }
  }

  useEffect(() => {
    if (
      !medicaoParaClonarId ||
      carregandoCadastro ||
      buscaAgendaClonagemIniciada.current ||
      !form.obraId ||
      !form.dataInicial ||
      !form.dataFinal
    ) {
      return;
    }

    buscaAgendaClonagemIniciada.current = true;
    carregarAgenda();
  }, [
    medicaoParaClonarId,
    carregandoCadastro,
    form.obraId,
    form.dataInicial,
    form.dataFinal,
  ]);

  function recalcularLinhas(proximoForm) {
    if (!moldagensObra.length) return proximoForm;
    const obra = obras.find((item) => item.id === proximoForm.obraId);
    return {
      ...proximoForm,
      linhas: criarLinhasAgenda(
        moldagensObra,
        obra,
        proximoForm.idadesSelecionadas,
        proximoForm.dataInicial,
        proximoForm.dataFinal,
        proximoForm.data,
        medicoes,
        medicaoEditando?.id
      ).map((linhaNova) => {
        const anterior = proximoForm.linhas.find(
          (linha) =>
            linha.id === linhaNova.id ||
            linha.agendaRupturaId === linhaNova.agendaRupturaId
        );
        return anterior
          ? {
              ...linhaNova,
              valorUnitario: anterior.valorUnitario,
              observacao: anterior.observacao,
            }
          : linhaNova;
      }),
    };
  }

  function alternarIdade(idade) {
    setForm((atual) => {
      const selecionadas = atual.idadesSelecionadas.includes(idade)
        ? atual.idadesSelecionadas.filter((item) => item !== idade)
        : [...atual.idadesSelecionadas, idade].sort((a, b) => numero(a) - numero(b));
      return recalcularLinhas({ ...atual, idadesSelecionadas: selecionadas });
    });
  }

  function atualizarLinha(index, campo, valor) {
    setForm((atual) => {
      const replicar =
        campo === "valorUnitario" &&
        replicarValorUnitarioAgenda &&
        index === 0;

      return {
        ...atual,
        linhas: atual.linhas.map((linha, linhaIndex) =>
          replicar || linhaIndex === index ? { ...linha, [campo]: valor } : linha
        ),
      };
    });
  }

  function alternarReplicacaoValorUnitario(ativo) {
    setReplicarValorUnitarioAgenda(ativo);

    if (!ativo) return;

    setForm((atual) => {
      const primeiroValor = atual.linhas[0]?.valorUnitario || "";
      return {
        ...atual,
        linhas: atual.linhas.map((linha) => ({
          ...linha,
          valorUnitario: primeiroValor,
        })),
      };
    });
  }

  function adicionarItem(tipo = "manual") {
    const novoItem = {
      id: `item-${Date.now()}`,
      tipo,
      descricao:
        tipo === "modulo_elasticidade"
          ? "ENSAIO DE MÓDULO DE ELASTICIDADE"
          : tipo === "locacao_equipamento" ? "LOCAÇÃO DE EQUIPAMENTO"
          : tipo === "consultoria" ? "CONSULTORIA" : "",
      ...(["locacao_equipamento", "consultoria"].includes(tipo) ? {
        selecionado: true,
        unidade: tipo === "locacao_equipamento" ? "Mensal" : "Hora",
      } : {}),
      quantidade: tipo === "modulo_elasticidade" ? 0 : 1,
      valorUnitario: "",
      moduloIds: [],
      codigosModulo: [],
    };

    setForm((atual) => ({
      ...atual,
      itensAdicionais: [...atual.itensAdicionais, novoItem],
    }));

    return form.itensAdicionais.length;
  }

  function atualizarItem(index, campo, valor) {
    setForm((atual) => ({
      ...atual,
      itensAdicionais: atual.itensAdicionais.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        if (campo === "tipo" && valor === "modulo_elasticidade") {
          return {
            ...item,
            tipo: valor,
            descricao: "ENSAIO DE MÓDULO DE ELASTICIDADE",
            quantidade: item.moduloIds?.length || 0,
          };
        }
        if (campo === "tipo") {
          return { ...item, tipo: valor, moduloIds: [], codigosModulo: [] };
        }
        return { ...item, [campo]: valor };
      }),
    }));
  }

  async function abrirSelecaoModulos(index) {
    if (!form.clienteId || !form.obraId) {
      alert("Selecione o cliente e a obra antes de pesquisar os módulos.");
      return;
    }

    setCarregandoModulos(true);
    try {
      const snap = await getDocs(consultaTenantAtual(collection(db, "tblModulosElasticidade")));
      setModulosDisponiveis(
        snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => {
            const mesmoCliente =
              item.clienteId === form.clienteId ||
              normalizarTexto(item.cliente) === normalizarTexto(form.cliente);
            const mesmaObra =
              item.obraId === form.obraId ||
              normalizarTexto(item.obra) === normalizarTexto(form.obra);
            return mesmoCliente && mesmaObra;
          })
          .sort((a, b) => String(a.codigo || "").localeCompare(String(b.codigo || ""), "pt-BR", { numeric: true }))
      );
      setFiltroModulos("");
      setDataInicialModulos("");
      setDataFinalModulos("");
      setItemModuloIndex(index);
    } catch (error) {
      console.error("Erro ao carregar módulos:", error);
      alert("Não foi possível carregar os módulos de elasticidade.");
    } finally {
      setCarregandoModulos(false);
    }
  }

  async function adicionarModuloElasticidade() {
    if (!form.clienteId || !form.obraId) {
      alert("Selecione o cliente e a obra antes de acrescentar módulos.");
      return;
    }
    const novoIndex = adicionarItem("modulo_elasticidade");
    await abrirSelecaoModulos(novoIndex);
  }

  function alternarModulo(modulo) {
    setForm((atual) => ({
      ...atual,
      itensAdicionais: atual.itensAdicionais.map((item, index) => {
        if (index !== itemModuloIndex) return item;
        const selecionado = item.moduloIds?.includes(modulo.id);
        const moduloIds = selecionado
          ? item.moduloIds.filter((id) => id !== modulo.id)
          : [...(item.moduloIds || []), modulo.id];
        const codigosModulo = selecionado
          ? (item.codigosModulo || []).filter((codigo) => codigo !== modulo.codigo)
          : [...(item.codigosModulo || []), modulo.codigo || modulo.id];
        return { ...item, moduloIds, codigosModulo, quantidade: moduloIds.length };
      }),
    }));
  }

  const totalAgenda = useMemo(
    () => form.linhas.reduce((total, linha) => total + totalLinha(linha, form.idadesSelecionadas), 0),
    [form.linhas, form.idadesSelecionadas]
  );
  const totalAdicionais = useMemo(
    () => form.itensAdicionais.reduce((total, item) => total + totalItem(item), 0),
    [form.itensAdicionais]
  );

  const modulosFiltrados = useMemo(() => {
    const termo = filtroModulos.trim().toLocaleLowerCase("pt-BR");
    return modulosDisponiveis.filter((item) =>
      {
        const dataEnsaio = item.dataEnsaio || item.dataRelatorio || item.dataMoldagem || "";
        if (!dentroDoPeriodo(dataEnsaio, dataInicialModulos, dataFinalModulos)) {
          return false;
        }
        if (!termo) return true;
        return [
          item.codigo,
          item.dataMoldagem,
          item.dataEnsaio,
          item.notaFiscalRomaneio,
        ].some((valor) => contemTextoPesquisa(String(valor || "").toLocaleLowerCase("pt-BR"), termo));
      }
    );
  }, [modulosDisponiveis, filtroModulos, dataInicialModulos, dataFinalModulos]);

  async function salvar() {
    const notificarAposDias = Number(form.notificarAposDias);
    if (
      form.notificarAposDias !== "" &&
      (!Number.isInteger(notificarAposDias) || notificarAposDias < 0)
    ) {
      alert("Informe uma quantidade válida de dias para a notificação.");
      return;
    }
    if (form.periodoOutrosServicosInicio && form.periodoOutrosServicosFim &&
        form.periodoOutrosServicosInicio > form.periodoOutrosServicosFim) {
      alert("A data final do período dos outros serviços deve ser igual ou posterior à data inicial.");
      return;
    }
    await executarSalvamento(async () => {
      if (!form.data) {
        alert("Informe a data da medição.");
        return;
      }
      if (!form.clienteId || !form.obraId) {
        alert("Selecione o cliente e a obra.");
        return;
      }
      if (!form.linhas.length && !form.itensAdicionais.length) {
        alert("Inclua itens da Agenda de Ruptura ou itens adicionais.");
        return;
      }

      try {
        const ano = new Date(`${form.data || hoje()}T00:00:00`).getFullYear();
        const codigo = medicaoEditando
          ? form.codigo
          : form.codigo ||
            (
              await gerarSequencial({
                chave: "medicao",
                escopo: ano,
                formato: { padding: 3, ano, template: "MED-{numero}/{ano}" },
                valorMinimo: medicoes.length
                  ? obterMaiorSequencial(medicoes, /MED-(\d+)\/\d{4}/)
                  : 99,
              })
            ).codigo;
        const dados = {
          ...form,
          notificarAposDias:
            form.notificarAposDias === "" ? "" : notificarAposDias,
          codigo,
          totalAgenda,
          totalAdicionais,
          totalGeral: totalAgenda + totalAdicionais,
          atualizadoEm: new Date(),
          atualizadoPor: usuario?.uid || null,
          atualizadoPorEmail: usuario?.email || null,
        };

        let medicaoSalva;
        if (medicaoEditando?.id) {
          const dadosSalvos = await atualizarDocumentoComMesclagem({
            db,
            referencia: doc(db, "tblMedicoes", medicaoEditando.id),
            original: medicaoEditando,
            atual: { ...medicaoEditando, ...dados },
            metadados: {
              atualizadoEm: dados.atualizadoEm,
              atualizadoPor: dados.atualizadoPor,
              atualizadoPorEmail: dados.atualizadoPorEmail,
            },
          });
          medicaoSalva = { id: medicaoEditando.id, ...dadosSalvos };
          setMedicoes((atual) =>
            atual.map((item) => (item.id === medicaoEditando.id ? medicaoSalva : item))
          );
        } else {
          const docRef = await addDoc(
            collection(db, "tblMedicoes"),
            aplicarTenantAtual({
              ...dados,
              criadoEm: new Date(),
              criadoPor: usuario?.uid || null,
              criadoPorEmail: usuario?.email || null,
            })
          );
          medicaoSalva = { id: docRef.id, ...dados };
          setMedicoes((atual) => [{ id: docRef.id, ...dados }, ...atual]);
        }

        const formAtualizado = medicaoEditando?.id
          ? { ...estadoInicial, ...medicaoSalva }
          : { ...form, codigo };
        setForm(formAtualizado);
        setFormSalvo(formAtualizado);
        setMedicaoEditando(medicaoSalva);
        setMensagem("Medição salva com sucesso.");
        if (!medicaoEditando?.id) {
          history.replace(`/app/medicoes/${medicaoSalva.id}`);
        }
      } catch (error) {
        console.error("Erro ao salvar medição:", error);
        alert("Não foi possível salvar a medição.");
      }
    });
  }

  async function abrirRelatorio() {
    let atual = medicaoEditando ? { ...medicaoEditando, ...form } : form;
    if (medicaoEditando?.id) {
      const snap = await getDoc(doc(db, "tblMedicoes", medicaoEditando.id));
      if (snap.exists()) atual = { id: snap.id, ...snap.data() };
    }
    setMedicaoPrint(atual);
  }

  function voltarDoRelatorio() {
    if (new URLSearchParams(location.search).get("origem") === "lista") {
      history.push("/app/medicoes");
      return;
    }
    setMedicaoPrint(null);
  }

  async function cancelarAlteracoes() {
    setMensagem("");
    setItemModuloIndex(null);

    if (!medicaoEditando?.id || !formSalvo) {
      history.push("/app/medicoes");
      return;
    }

    await carregarObras(formSalvo.clienteId);
    setForm(formSalvo);
    setMoldagensObra([]);
  }

  if (medicaoPrint) {
    return <RelatorioMedicaoPrint medicao={medicaoPrint} onVoltar={voltarDoRelatorio} />;
  }

  if (carregandoCadastro) {
    return <><Navbar /><Box display="flex" justifyContent="center" p={8}><CircularProgress /></Box></>;
  }

  const todasIdades = Array.from(
    new Set(moldagensObra.flatMap((item) => (item.idades || []).map(normalizarIdade).filter(Boolean)))
  ).sort((a, b) => numero(a) - numero(b));
  const temAlteracoes = !medicaoEditando?.id || JSON.stringify(form) !== JSON.stringify(formSalvo);

  return (
    <>
      <Navbar />
      <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, pt: 0, mt: { xs: "-8px", md: "-16px" }, bgcolor: "#f3f6fa", minHeight: "100vh" }}>
        <Paper sx={{ position: "sticky", top: "60px", zIndex: 1100, bgcolor: "background.paper", p: 2, mb: 2, display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { md: "center" }, gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Cadastro de Medição</Typography>
            <Typography variant="body2" color="text.secondary">
              {form.codigo || "Nova medição"} · {dataBR(form.data)}
            </Typography>
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Tooltip title="Visualizar relatório">
              <span><IconButton color="primary" disabled={!medicaoEditando?.id || temAlteracoes} onClick={abrirRelatorio}><VisibilityIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title="Imprimir relatório">
              <span><IconButton color="primary" disabled={!medicaoEditando?.id || temAlteracoes} onClick={abrirRelatorio}><PrintIcon /></IconButton></span>
            </Tooltip>
            <Button startIcon={<ArrowBackIcon />} disabled={temAlteracoes} onClick={() => history.push("/app/medicoes")}>Voltar</Button>
            {temAlteracoes && (
              <>
                <Button variant="contained" startIcon={salvandoCadastro ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} disabled={salvandoCadastro} onClick={salvar}>
                  {salvandoCadastro ? "Salvando..." : "Salvar"}
                </Button>
                <Button variant="outlined" disabled={salvandoCadastro} onClick={cancelarAlteracoes}>
                  Cancelar alterações
                </Button>
              </>
            )}
          </Stack>
        </Paper>
        {mensagem && <Typography color="primary" mb={2}>{mensagem}</Typography>}

        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
            <Autocomplete
              sx={{ gridColumn: { md: "span 2" } }}
              options={clientes}
              value={clientes.find((item) => item.id === form.clienteId) || null}
              getOptionLabel={(item) => item.nome || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, cliente) => selecionarCliente(cliente)}
              renderInput={(params) => <TextField {...params} label="Cliente" />}
            />
            <Autocomplete
              sx={{ gridColumn: { md: "span 2" } }}
              options={obras}
              value={obras.find((item) => item.id === form.obraId) || null}
              getOptionLabel={(item) => item.nome || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, obra) => selecionarObra(obra)}
              disabled={!form.clienteId}
              renderInput={(params) => <TextField {...params} label="Obra" />}
            />
            <Box sx={{ px: 1, py: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Código</Typography>
              <Typography fontWeight={700}>{form.codigo || "Gerado automaticamente ao salvar"}</Typography>
            </Box>
            <TextField label="Data da medição" type="date" value={form.data} InputLabelProps={{ shrink: true }} onChange={(e) => setForm((atual) => recalcularLinhas({ ...atual, data: e.target.value }))} />
            <TextField label="Moldagens de" type="date" value={form.dataInicial} InputLabelProps={{ shrink: true }} onChange={(e) => setForm((atual) => recalcularLinhas({ ...atual, dataInicial: e.target.value }))} />
            <Box>
              <TextField label="Moldagens até" type="date" value={form.dataFinal} InputLabelProps={{ shrink: true }} onChange={(e) => setForm((atual) => recalcularLinhas({ ...atual, dataFinal: e.target.value }))} fullWidth />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.dataFinal === hoje()}
                    onChange={(event) => setForm((atual) => recalcularLinhas({
                      ...atual,
                      dataFinal: event.target.checked ? hoje() : "",
                    }))}
                  />
                }
                label="Usar data de hoje"
                sx={{ mt: 0.25, ml: -0.5 }}
              />
            </Box>
            <Button variant="contained" startIcon={carregandoAgenda ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />} disabled={!form.obraId || !form.dataInicial || !form.dataFinal || carregandoAgenda} onClick={carregarAgenda}>
              {carregandoAgenda ? "Buscando agenda..." : "Buscar Agenda de Ruptura"}
            </Button>
          </Box>

          {todasIdades.length > 0 && (
            <Box mt={2}>
              <Typography fontWeight={700}>Idades que entram na medição</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {todasIdades.map((idade) => (
                  <FormControlLabel key={idade} control={<Checkbox checked={form.idadesSelecionadas.includes(idade)} onChange={() => alternarIdade(idade)} />} label={`${idade} dias`} />
                ))}
              </Stack>
            </Box>
          )}

          <TableContainer sx={{ mt: 2, border: "1px solid #dde3ea" }}>
            {form.linhas.length > 1 && (
              <Box sx={{ px: 1.5, pt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={replicarValorUnitarioAgenda}
                      onChange={(event) => alternarReplicacaoValorUnitario(event.target.checked)}
                    />
                  }
                  label="Repetir o primeiro valor unitário nos demais códigos"
                />
              </Box>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Código</TableCell>
                  <TableCell align="center">Data da moldagem</TableCell>
                  {form.idadesSelecionadas.map((idade) => <TableCell align="center" key={idade}>{idade} dias</TableCell>)}
                  <TableCell align="center" sx={{ width: 145 }}>Valor unitário</TableCell>
                  <TableCell align="right" sx={{ width: 145 }}>Valor total</TableCell>
                  <TableCell sx={{ minWidth: 320 }}>Observação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.linhas.map((linha, index) => (
                  <TableRow key={linha.id}>
                    <TableCell align="center">{linha.codigo}</TableCell>
                    <TableCell align="center">{dataBR(linha.dataMoldagem)}</TableCell>
                    {form.idadesSelecionadas.map((idade) => <TableCell align="center" key={idade}>{linha.quantidades?.[idade] || ""}</TableCell>)}
                    <TableCell align="center" sx={{ width: 145 }}><TextField size="small" value={linha.valorUnitario} onChange={(e) => atualizarLinha(index, "valorUnitario", e.target.value)} onBlur={(e) => atualizarLinha(index, "valorUnitario", formatarValorUnitario(e.target.value))} onKeyDown={(e) => moverParaCampoAbaixo(e, "agenda-valor-unitario")} disabled={replicarValorUnitarioAgenda && index > 0} inputProps={{ inputMode: "decimal", "data-medicao-coluna": "agenda-valor-unitario", style: { textAlign: "center" } }} sx={{ width: 110 }} /></TableCell>
                    <TableCell align="right">{moeda(totalLinha(linha, form.idadesSelecionadas))}</TableCell>
                    <TableCell sx={{ minWidth: 320 }}><TextField fullWidth size="small" value={linha.observacao || ""} onChange={(e) => atualizarLinha(index, "observacao", e.target.value)} onKeyDown={(e) => moverParaCampoAbaixo(e, "agenda-observacao")} inputProps={{ "data-medicao-coluna": "agenda-observacao" }} /></TableCell>
                  </TableRow>
                ))}
                {!form.linhas.length && <TableRow><TableCell colSpan={5 + form.idadesSelecionadas.length} align="center">{agendaConsultada ? "Nenhum CP rompido está disponível neste período. Verifique se os resultados já foram medidos anteriormente." : "Selecione cliente e obra e busque a Agenda de Ruptura."}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #dde3ea" }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} gap={1}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Outros serviços da medição</Typography>
                <Typography variant="body2" color="text.secondary">
                  Pesquise módulos já cadastrados ou inclua um serviço avulso sem cadastro.
                </Typography>
              </Box>
              <Typography fontWeight={700}>Parcial dos CPs: {moeda(totalAgenda)}</Typography>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1} mt={2}>
              <Button
                variant="contained"
                startIcon={carregandoModulos ? <CircularProgress size={18} color="inherit" /> : <ScienceIcon />}
                disabled={carregandoModulos || !form.clienteId || !form.obraId}
                onClick={adicionarModuloElasticidade}
              >
                {carregandoModulos ? "Pesquisando módulos..." : "Acrescentar módulo de elasticidade"}
              </Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => adicionarItem("manual")}>
                Acrescentar serviço avulso
              </Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => adicionarItem("locacao_equipamento")}>
                Acrescentar locação de equipamento
              </Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => adicionarItem("consultoria")}>
                Acrescentar consultoria
              </Button>
            </Stack>
          </Box>

          {form.itensAdicionais.map((item, index) => (
            <Paper key={item.id || index} variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: ["locacao_equipamento", "consultoria"].includes(item.tipo) ? "minmax(220px, 1fr) 100px 120px 150px 130px 48px" : "minmax(260px, 1fr) 130px 170px 150px 48px" }, gap: 1, alignItems: "center" }}>
              {item.tipo === "modulo_elasticidade" ? (
                <Box>
                  <Typography fontWeight={700}>Módulo de elasticidade</Typography>
                  <Button size="small" variant="outlined" onClick={() => abrirSelecaoModulos(index)} sx={{ mt: 0.5 }}>
                    {item.moduloIds?.length ? "Alterar módulos selecionados" : "Selecionar módulos cadastrados"}
                  </Button>
                  {item.codigosModulo?.length > 0 && (
                    <Typography variant="caption" display="block" mt={0.5}>
                      Códigos selecionados: {item.codigosModulo.join(", ")}
                    </Typography>
                  )}
                </Box>
              ) : (
                <TextField size="small" label={item.tipo === "locacao_equipamento" ? "Locação de equipamento" : item.tipo === "consultoria" ? "Consultoria" : "Descrição do serviço avulso"} value={item.descricao || ""} onChange={(e) => atualizarItem(index, "descricao", e.target.value)} onKeyDown={(e) => moverParaCampoAbaixo(e, "servico-descricao")} inputProps={{ "data-medicao-coluna": "servico-descricao" }} />
              )}
              {item.tipo === "modulo_elasticidade" ? (
                <Box sx={{ px: 1 }}>
                  <Typography variant="caption" color="text.secondary">Quantidade</Typography>
                  <Typography fontWeight={700}>{item.quantidade || 0}</Typography>
                </Box>
              ) : (
                <TextField size="small" label="Quantidade" value={item.quantidade} onChange={(e) => atualizarItem(index, "quantidade", e.target.value)} onKeyDown={(e) => moverParaCampoAbaixo(e, "servico-quantidade")} inputProps={{ inputMode: "decimal", "data-medicao-coluna": "servico-quantidade" }} />
              )}
              {["locacao_equipamento", "consultoria"].includes(item.tipo) && (
                <TextField select size="small" label="Unidade" value={item.unidade || "Unidade"}
                  onChange={(e) => atualizarItem(index, "unidade", e.target.value)}>
                  {["Mensal", "Diária", "Semanal", "Hora", "Unidade"].map((unidade) =>
                    <MenuItem key={unidade} value={unidade}>{unidade}</MenuItem>)}
                </TextField>
              )}
              <TextField size="small" label="Valor unitário" value={item.valorUnitario} onChange={(e) => atualizarItem(index, "valorUnitario", e.target.value)} onBlur={(e) => atualizarItem(index, "valorUnitario", formatarValorUnitario(e.target.value))} onKeyDown={(e) => moverParaCampoAbaixo(e, "servico-valor-unitario")} inputProps={{ inputMode: "decimal", "data-medicao-coluna": "servico-valor-unitario" }} />
              <Typography textAlign="right" fontWeight={700}>{moeda(totalItem(item))}</Typography>
              <Tooltip title="Remover"><IconButton color="error" onClick={() => setForm((atual) => ({ ...atual, itensAdicionais: atual.itensAdicionais.filter((_, itemIndex) => itemIndex !== index) }))}><DeleteOutlineIcon /></IconButton></Tooltip>
              </Box>
            </Paper>
          ))}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2, mt: 2 }}>
            <TextField label="Período dos outros serviços — de" type="date" InputLabelProps={{ shrink: true }}
              value={form.periodoOutrosServicosInicio || ""}
              onChange={(e) => setForm((atual) => ({ ...atual, periodoOutrosServicosInicio: e.target.value }))} />
            <TextField label="Até" type="date" InputLabelProps={{ shrink: true }}
              value={form.periodoOutrosServicosFim || ""}
              inputProps={{ min: form.periodoOutrosServicosInicio || undefined }}
              onChange={(e) => setForm((atual) => ({ ...atual, periodoOutrosServicosFim: e.target.value }))} />
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <TextField
                label="Notificar após (dias)"
                type="number"
                value={form.notificarAposDias}
                disabled={Boolean(form.notificacaoCancelada)}
                inputProps={{ min: 0, step: 1 }}
                helperText={`Previsão da próxima medição: ${calcularPrevisaoProximaMedicao(
                  form.periodoOutrosServicosFim,
                  form.notificarAposDias
                )}`}
                onChange={(event) =>
                  setForm((atual) => ({
                    ...atual,
                    notificacaoCancelada: false,
                    notificarAposDias: event.target.value,
                  }))
                }
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.notificacaoCancelada)}
                    onChange={(event) =>
                      setForm((atual) => ({
                        ...atual,
                        notificacaoCancelada: event.target.checked,
                        notificarAposDias: event.target.checked
                          ? ""
                          : atual.notificarAposDias,
                      }))
                    }
                  />
                }
                label="Cancelar notificação"
                sx={{ m: 0, whiteSpace: "nowrap" }}
              />
            </Box>
            <TextField label="Medição conforme relatório" value={form.referenciaRelatorio} onChange={(e) => setForm({ ...form, referenciaRelatorio: e.target.value })} />
            <TextField label="Observações gerais" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} justifyContent="flex-end" alignItems={{ md: "center" }} gap={2} mt={3}>
            <Typography variant="h6" fontWeight={800}>Valor total da medição: {moeda(totalAgenda + totalAdicionais)}</Typography>
          </Stack>
        </Paper>
      </Box>

      <Dialog open={itemModuloIndex !== null} onClose={() => setItemModuloIndex(null)} fullWidth maxWidth="md">
        <DialogTitle>Selecionar ensaios de módulo de elasticidade</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Ensaios cadastrados para <strong>{form.obra}</strong>, cliente {form.cliente}.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data do ensaio de"
              InputLabelProps={{ shrink: true }}
              value={dataInicialModulos}
              onChange={(event) => setDataInicialModulos(event.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data do ensaio até"
              InputLabelProps={{ shrink: true }}
              value={dataFinalModulos}
              onChange={(event) => setDataFinalModulos(event.target.value)}
            />
            <Button
              variant="outlined"
              disabled={!dataInicialModulos && !dataFinalModulos}
              onClick={() => {
                setDataInicialModulos("");
                setDataFinalModulos("");
              }}
              sx={{ minWidth: 140 }}
            >
              Limpar período
            </Button>
          </Stack>
          <TextField
            fullWidth
            size="small"
            label="Pesquisar por código, data ou nota/romaneio"
            name="pesquisa-modulos-medicao"
            autoComplete="new-password"
            value={filtroModulos}
            onChange={(e) => setFiltroModulos(e.target.value)}
            inputProps={{ autoComplete: "new-password" }}
            sx={{ mb: 2 }}
          />
          {!modulosFiltrados.length ? (
            <Typography>Nenhum ensaio de módulo de elasticidade foi encontrado para esta obra.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Selecionar</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Data da moldagem</TableCell>
                    <TableCell>Data do ensaio</TableCell>
                    <TableCell>Nota/Romaneio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modulosFiltrados.map((modulo) => {
                    const selecionados = form.itensAdicionais[itemModuloIndex]?.moduloIds || [];
                    const selecionado = selecionados.includes(modulo.id);
                    return (
                      <TableRow
                        hover
                        key={modulo.id}
                        selected={selecionado}
                        sx={{ cursor: "pointer" }}
                        onClick={() => alternarModulo(modulo)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selecionado}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => alternarModulo(modulo)}
                          />
                        </TableCell>
                        <TableCell>{modulo.codigo || "Sem código"}</TableCell>
                        <TableCell>{dataBR(modulo.dataMoldagem)}</TableCell>
                        <TableCell>{dataBR(modulo.dataEnsaio || modulo.dataRelatorio)}</TableCell>
                        <TableCell>{modulo.notaFiscalRomaneio || "-"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setItemModuloIndex(null)}>Concluir seleção</Button></DialogActions>
      </Dialog>
    </>
  );
}
